import type { ProposalNodeSet, TimberListItem } from '../types';

/**
 * Validates dimensions string used on proposal timber lines (e.g. "4x8x12").
 */
export function validateTimberDimensionsString(dim: string | undefined): string | null {
  if (!dim?.trim()) return 'Missing dimensions';
  const parts = dim
    .toLowerCase()
    .split(/[x×]/i)
    .map(s => s.trim())
    .filter(Boolean);
  if (parts.length < 3) {
    return 'Dimensions should be height×width×length (e.g. 4×8×12)';
  }
  for (const p of parts) {
    const n = parseFloat(p.replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(n) || n <= 0) {
      return 'Invalid piece size in dimensions';
    }
  }
  return null;
}

/** Validation messages for a single timber line (piece size, quantity, etc.). */
export function validateTimberListItem(item: TimberListItem): string[] {
  const messages: string[] = [];
  if (!item.name?.trim()) messages.push('Missing piece name');
  if (!item.species?.trim()) messages.push('Missing species/material');
  if (!Number.isFinite(item.quantity) || item.quantity < 1) {
    messages.push('Quantity must be at least 1');
  }
  if (!Number.isFinite(item.boardFeet) || item.boardFeet <= 0) {
    messages.push('Board feet must be greater than zero');
  }
  const dimErr = validateTimberDimensionsString(item.dimensions);
  if (dimErr) messages.push(dimErr);
  return messages;
}

/**
 * Order length in feet from timber `dimensions` (third segment after splitting on ×).
 * Matches CSV `normalizedDimensions` format: `h×w×L` with L in feet.
 */
export function parseOrderLengthFeetFromDimensions(dim: string | undefined): number | null {
  if (!dim?.trim()) return null;
  const parts = dim
    .toLowerCase()
    .split(/[x×]/i)
    .map(s => s.trim())
    .filter(Boolean);
  if (parts.length < 3) return null;
  const n = parseFloat(parts[2].replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** Any line with order length strictly under 8' (supplier order minimum). */
export function timberLineHasOrderLengthUnder8Feet(item: TimberListItem): boolean {
  const len = parseOrderLengthFeetFromDimensions(item.dimensions);
  if (len === null) return false;
  return len < 8;
}

function listHasOrderLengthUnder8Feet(list: TimberListItem[]): boolean {
  return list.some(timberLineHasOrderLengthUnder8Feet);
}

/** True if any timber line (default or alternate species) has order length &lt; 8'. */
export function structureHasShortOrderLengthTimber(nodeSet: ProposalNodeSet): boolean {
  if (listHasOrderLengthUnder8Feet(nodeSet.materials?.timberList ?? [])) return true;
  for (const alt of nodeSet.materials?.alternateSpecies ?? []) {
    if (listHasOrderLengthUnder8Feet(alt.timberList ?? [])) return true;
  }
  return false;
}

/** Same idea as StructureDetailModal timber list header ("Pricing Missing"): no unit price from materials DB yet. */
export function timberLineNeedsPricing(item: TimberListItem): boolean {
  const u = item.unitCost;
  return u === undefined || u === null || !Number.isFinite(u) || u === 0;
}

function listHasValidationIssues(list: TimberListItem[]): boolean {
  return list.some(item => validateTimberListItem(item).length > 0);
}

function listHasPricingIssues(list: TimberListItem[]): boolean {
  return list.some(timberLineNeedsPricing);
}

/** Piece/dimension validation only (used before supplier RFQ export — PDF has no $). */
export function structureHasTimberListValidationIssues(nodeSet: ProposalNodeSet): boolean {
  if (listHasValidationIssues(nodeSet.materials?.timberList ?? [])) return true;
  for (const alt of nodeSet.materials?.alternateSpecies ?? []) {
    if (listHasValidationIssues(alt.timberList ?? [])) return true;
  }
  return false;
}

/** Matches materials UI: any line with unit cost $0 still needs database pricing. */
export function structureHasTimberListPricingIssues(nodeSet: ProposalNodeSet): boolean {
  if (listHasPricingIssues(nodeSet.materials?.timberList ?? [])) return true;
  for (const alt of nodeSet.materials?.alternateSpecies ?? []) {
    if (listHasPricingIssues(alt.timberList ?? [])) return true;
  }
  return false;
}

/**
 * Structure card / readiness: validation problems OR missing pricing.
 * Aligns with the "⚠ Pricing Missing" warning inside the structure detail modal.
 */
export function structureHasTimberListIssues(nodeSet: ProposalNodeSet): boolean {
  return structureHasTimberListValidationIssues(nodeSet) || structureHasTimberListPricingIssues(nodeSet);
}

export function proposalHasAnyTimberListIssues(nodeSets: ProposalNodeSet[]): boolean {
  return nodeSets.some(structureHasTimberListIssues);
}

/** Names of structures that still have dimension/field problems (for RFQ export guard). */
export function getStructureNamesWithTimberValidationIssues(nodeSets: ProposalNodeSet[]): string[] {
  return nodeSets.filter(structureHasTimberListValidationIssues).map(ns => ns.name);
}

/** Names of structures with validation and/or pricing issues (import modals, etc.). */
export function getStructureNamesWithTimberListIssues(nodeSets: ProposalNodeSet[]): string[] {
  return nodeSets.filter(structureHasTimberListIssues).map(ns => ns.name);
}

/** Structures that have at least one line with order length &lt; 8'. */
export function getStructureNamesWithShortOrderLengthTimber(nodeSets: ProposalNodeSet[]): string[] {
  return nodeSets.filter(structureHasShortOrderLengthTimber).map(ns => ns.name);
}
